import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { FileDown, Search, RefreshCw, ShieldCheck } from "lucide-react";

const RESOURCE_TYPES = ["", "contract", "user", "vendor", "site", "workflow_template", "eval_rule",
                        "contract_evaluation", "folder", "auth", "settings"];

export default function AuditViewer() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [resType, setResType] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const params = [];
      if (resType) params.push(`resource_type=${encodeURIComponent(resType)}`);
      if (q.trim()) params.push(`actor=${encodeURIComponent(q.trim())}`);
      params.push("limit=1000");
      const { data } = await api.get(`/audit?${params.join("&")}`);
      setRows(data || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load audit"); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const exportCSV = () => {
    const header = ["at","actor","actor_role","action","resource_type","resource_id","contract_number","meta"];
    const lines = [header.join(",")];
    rows.forEach(e => {
      const cell = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
      lines.push([
        cell(e.at), cell(e.actor), cell(e.actor_role), cell(e.action),
        cell(e.resource_type), cell(e.resource_id), cell(e.contract_number),
        cell(JSON.stringify(e.meta || {})),
      ].join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `audit-${new Date().toISOString().slice(0,10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} rows`);
  };

  const exportPDF = async () => {
    setBusy(true);
    try {
      const params = new URLSearchParams();
      if (resType) params.set("resource_type", resType);
      if (q.trim()) params.set("actor", q.trim());
      params.set("limit", "1000");
      const res = await api.get(`/audit/pdf?${params.toString()}`, { responseType: "blob", timeout: 120000 });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `hcd-audit-${new Date().toISOString().slice(0,10)}.pdf`;
      a.click(); URL.revokeObjectURL(url);
      toast.success("PDF ready");
    } catch (e) { toast.error("PDF export failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="p-6" data-testid="audit-viewer-page">
      <div className="flex items-center gap-3 mb-4">
        <ShieldCheck className="w-5 h-5 text-[#005289]"/>
        <div>
          <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Phase 14 · Audit</div>
          <h1 className="text-2xl font-bold text-slate-900">Immutable Audit Viewer</h1>
          <p className="text-sm text-slate-500 mt-1">Every write across the app — auth, role changes, contract stage, tracker edits, evaluations, milestones, signatures.</p>
        </div>
        <button onClick={load} disabled={busy} data-testid="audit-reload"
                className="ml-auto h-8 px-3 text-xs border border-slate-200 rounded hover:bg-slate-50 flex items-center gap-1">
          <RefreshCw className="w-3 h-3"/> {busy ? "Loading…" : "Reload"}
        </button>
        <button onClick={exportCSV} data-testid="audit-export"
                className="h-8 px-3 text-xs rounded bg-[#005289] text-white flex items-center gap-1">
          <FileDown className="w-3 h-3"/> Export CSV
        </button>
        <button onClick={exportPDF} disabled={busy} data-testid="audit-export-pdf"
                className="h-8 px-3 text-xs rounded bg-red-700 text-white flex items-center gap-1 disabled:opacity-60">
          <FileDown className="w-3 h-3"/> Export PDF
        </button>
      </div>

      <div className="flex gap-2 mb-3">
        <div className="relative flex-1 max-w-md">
          <Search className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400"/>
          <input value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => e.key === "Enter" && load()}
                 placeholder="Filter by actor (name)…" data-testid="audit-search"
                 className="w-full h-9 pl-7 pr-3 border border-slate-200 rounded text-xs"/>
        </div>
        <select value={resType} onChange={e => setResType(e.target.value)} data-testid="audit-type-filter"
                className="h-9 px-2 border border-slate-200 rounded text-xs">
          <option value="">All resource types</option>
          {RESOURCE_TYPES.filter(x => x).map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <button onClick={load} className="h-9 px-3 text-xs border border-slate-200 rounded hover:bg-slate-50">Apply</button>
      </div>

      <div className="border border-slate-200 rounded-md bg-white overflow-x-auto">
        <table className="hcd-table text-xs w-full">
          <thead>
            <tr>
              <th className="whitespace-nowrap">Timestamp</th>
              <th>Actor</th>
              <th>Action</th>
              <th>Resource</th>
              <th>Contract #</th>
              <th>Meta</th>
            </tr>
          </thead>
          <tbody data-testid="audit-rows">
            {rows.length === 0 ? (
              <tr><td colSpan={6} className="p-8 text-center text-slate-400">{busy ? "Loading…" : "No audit events match your filter."}</td></tr>
            ) : rows.slice(0, 500).map((e, i) => (
              <tr key={i} data-testid={`audit-row-${i}`}>
                <td className="font-mono text-[10px] whitespace-nowrap">{e.at ? new Date(e.at).toLocaleString() : "—"}</td>
                <td><div className="font-semibold">{e.actor || "—"}</div>{e.actor_role ? <div className="text-[10px] text-slate-500">{e.actor_role}</div> : null}</td>
                <td className="font-mono text-[11px]">{e.action}</td>
                <td className="text-slate-600">{e.resource_type || "—"}</td>
                <td className="font-mono text-[10px]">{e.contract_number || "—"}</td>
                <td className="text-[10px] text-slate-500 max-w-md truncate" title={JSON.stringify(e.meta || {})}>
                  {Object.keys(e.meta || {}).length ? JSON.stringify(e.meta).slice(0, 120) : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length > 500 ? <div className="p-2 text-center text-[10px] text-slate-400">Showing first 500 of {rows.length} — narrow filters or export CSV for the full set.</div> : null}
      </div>
    </div>
  );
}
